/*
  Card colour swatches. Scuba Evolution, 2026-09-15.

  On a product card (collection, search, recommendations), a colour swatch is a
  selector, not a link: clicking or tapping it shows that colour's variant photo on
  the card, and the card keeps that colour when the customer goes on to the product
  page or the quick view.

  Markup contract (snippets/product-card.liquid via snippets/swatch.liquid):
    a[data-card-swatch][data-variant-id]   role="button", aria-pressed
      [data-card-image]                    variant photo URL with a &width= param
      [data-card-image-width]              its width, capped at 1080
      [data-card-image-alt]
  A colour with no variant photo has no data-card-image; the card then shows the
  product's own featured image.

  The href stays on the swatch, so a modifier or middle click still opens that
  colour's product page in a new tab, and the page works without this script.

  Quick view: theme.js QuickView fetches data-product-url.split('?')[0] + '?view=modal',
  which drops any variant. When the card has a colour selected, the patch below adds
  &variant=<id>; main-product-modal then renders that variant checked with its photo
  first. Without a selection the theme's own method runs untouched.

  Fitting the row (2026-09-30): see fit() below.
*/
(function () {
  'use strict';

  if (window.__cardSwatch) return;
  window.__cardSwatch = true;

  var WIDTHS = [180, 360, 540, 720, 900, 1080];
  var SWATCH = 'a[data-card-swatch]';

  function srcsetFor(url, maxWidth) {
    var widths = WIDTHS.filter(function (w) { return w <= maxWidth; });
    if (!widths.length) widths = [maxWidth];
    return widths.map(function (w) {
      return (/[?&]width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, '$1width=' + w) : url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w) + ' ' + w + 'w';
    }).join(', ');
  }

  // The card's own image: a direct child of the media link. Absent when the featured
  // media is a video, in which case only the links change.
  function cardImage(card) {
    return card.querySelector('.product-card__media > a.media > img');
  }

  function remember(img) {
    if (img.hasAttribute('data-card-original-src')) return;
    img.setAttribute('data-card-original-src', img.getAttribute('src') || '');
    img.setAttribute('data-card-original-srcset', img.getAttribute('srcset') || '');
    img.setAttribute('data-card-original-alt', img.getAttribute('alt') || '');
  }

  function apply(img, src, srcset, alt) {
    // LazyImage (vendor.js) re-arms its loading veil (.media.loading, image at opacity 0)
    // when src/srcset change on an image not marked loaded. The file is already decoded,
    // so mark it loaded and lift any veil left from a lazy image that never loaded.
    img.classList.add('loaded');
    if (srcset) img.setAttribute('srcset', srcset); else img.removeAttribute('srcset');
    img.setAttribute('src', src);
    img.setAttribute('alt', alt);
    var media = img.parentElement;
    if (media && media.classList.contains('loading')) media.classList.remove('loading');
  }

  function showImage(card, swatch) {
    var img = cardImage(card);
    if (!img) return Promise.resolve();
    remember(img);

    var url = swatch.getAttribute('data-card-image');
    var src, srcset, alt;
    if (url) {
      var width = parseInt(swatch.getAttribute('data-card-image-width'), 10) || 1080;
      src = url;
      srcset = srcsetFor(url, width);
      alt = swatch.getAttribute('data-card-image-alt') || img.getAttribute('data-card-original-alt');
    } else {
      src = img.getAttribute('data-card-original-src');
      srcset = img.getAttribute('data-card-original-srcset');
      alt = img.getAttribute('data-card-original-alt');
    }

    var token = String(Date.now()) + Math.random();
    card.__cardSwatchToken = token;

    // Decode off-screen with the card's own sizes, so the browser picks the same
    // candidate the card will and the swap lands without a blank frame.
    var probe = new Image();
    if (img.sizes) probe.sizes = img.sizes;
    if (srcset) probe.srcset = srcset;
    probe.src = src;
    var ready = probe.decode ? probe.decode() : new Promise(function (resolve, reject) {
      probe.onload = resolve;
      probe.onerror = reject;
    });

    // decode() can reject for reasons other than a broken file; a loaded bitmap is enough.
    return ready.catch(function (error) {
      if (!(probe.complete && probe.naturalWidth > 0)) throw error;
    }).then(function () {
      if (card.__cardSwatchToken !== token) return;
      apply(img, src, srcset, alt);
      card.setAttribute('data-card-colour', swatch.getAttribute('data-variant-id'));
    }, function () {
      // The colour's photo failed to load: show the product's own image rather than
      // leave the previous colour's photo under this colour's ring.
      if (card.__cardSwatchToken !== token || !url) return;
      apply(img, img.getAttribute('data-card-original-src'), img.getAttribute('data-card-original-srcset'), img.getAttribute('data-card-original-alt'));
      card.setAttribute('data-card-colour', swatch.getAttribute('data-variant-id'));
    });
  }

  function carryVariant(card, swatch) {
    var variantId = swatch.getAttribute('data-variant-id');
    var productPath;
    try {
      productPath = new URL(swatch.getAttribute('href'), window.location.origin).pathname;
    } catch (e) {
      return;
    }

    Array.prototype.forEach.call(card.querySelectorAll('a[href]'), function (link) {
      if (link.matches(SWATCH)) return;
      var url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (e) {
        return;
      }
      if (url.origin !== window.location.origin || url.pathname !== productPath) return;
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', url.pathname + url.search + url.hash);
    });

    // The quick view element moves to <body> while open, so find it by id.
    var control = card.querySelector('[aria-controls^="Quickview-"]');
    var quickView = control && document.getElementById(control.getAttribute('aria-controls'));
    if (quickView) quickView.setAttribute('data-variant-id', variantId);
  }

  function select(swatch) {
    var card = swatch.closest('.product-card');
    if (!card) return;

    var list = swatch.closest('.swatches') || card;
    Array.prototype.forEach.call(list.querySelectorAll(SWATCH), function (other) {
      other.setAttribute('aria-pressed', other === swatch ? 'true' : 'false');
    });

    carryVariant(card, swatch);
    showImage(card, swatch);
  }

  document.addEventListener('click', function (event) {
    var swatch = event.target && event.target.closest ? event.target.closest(SWATCH) : null;
    if (!swatch) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    select(swatch);
  });

  // role="button": Space activates as well as Enter (Enter already fires click on a link).
  document.addEventListener('keydown', function (event) {
    if (event.key !== ' ' && event.key !== 'Spacebar') return;
    var swatch = event.target && event.target.closest ? event.target.closest(SWATCH) : null;
    if (!swatch) return;
    event.preventDefault();
    select(swatch);
  });

  /*
    Fitting the row to the card (2026-09-30). The card renders up to the theme's colour
    limit (Theme settings, product cards, 6 swatches), but at 24px a phone card or a
    four-across desktop card only has room for three or four, and the row spilled past
    both edges of the card. fit() keeps as many swatches as the row's width holds and
    folds the rest into the +N chip, whose number is the product's full colour count
    (data-colour-count) less the swatches still showing. A picked colour is never
    folded away: it swaps into the last visible spot.

    Each row is watched by a ResizeObserver, so it re-fits when the filters open or
    close, a phone turns, or the grid changes its column count. Rows that arrive later
    (filtering, pagination, recommendations) are picked up by a MutationObserver.

    Markup contract (snippets/product-card.liquid):
      [data-card-swatch-row][data-colour-count]   the row
        ul.swatches > li                          one per rendered colour, in-stock first
        a[data-card-swatch-more]                  the +N chip, a link to the product page
  */
  var ROW = '[data-card-swatch-row]';

  function fit(row) {
    var list = row.querySelector('.swatches');
    var more = row.querySelector('[data-card-swatch-more]');
    if (!list || !more || !list.children.length) return;

    var style = getComputedStyle(row);
    var available = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (available <= 0) return; // Not laid out yet (a closed tab or drawer): fit it when it shows.

    var items = Array.prototype.slice.call(list.children);
    items.forEach(function (item) { item.hidden = false; });
    var total = parseInt(row.getAttribute('data-colour-count'), 10) || items.length;
    var size = items[0].getBoundingClientRect().width;
    var gap = parseFloat(getComputedStyle(list).columnGap) || 0;

    // The chip at its widest, which is with a single swatch left showing.
    more.hidden = false;
    more.textContent = '+' + Math.max(total - 1, 1);
    var chip = more.getBoundingClientRect().width + (parseFloat(style.columnGap) || 0);

    var shown = items.length;
    var needed = function (count) {
      return count * size + (count - 1) * gap + (count < total ? chip : 0);
    };
    while (shown > 1 && needed(shown) > available + 0.5) shown--;

    var picked = list.querySelector('[aria-pressed="true"]');
    var pickedItem = picked ? picked.closest('li') : null;
    if (pickedItem && items.indexOf(pickedItem) >= shown) {
      list.insertBefore(pickedItem, items[shown - 1]);
      items = Array.prototype.slice.call(list.children);
    }

    items.forEach(function (item, index) { item.hidden = index >= shown; });
    var folded = total - shown;
    more.hidden = folded < 1;
    more.textContent = '+' + folded;
    more.setAttribute('aria-label', folded + (folded === 1 ? ' more colour' : ' more colours'));
    row.setAttribute('data-fitted', '');
  }

  if (window.ResizeObserver) {
    var resizeObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) { fit(entry.target); });
    });

    var watch = function (root) {
      if (root.matches && root.matches(ROW)) resizeObserver.observe(root);
      if (root.querySelectorAll) Array.prototype.forEach.call(root.querySelectorAll(ROW), function (row) { resizeObserver.observe(row); });
    };

    watch(document);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1) watch(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function patchQuickView() {
    var QuickView = customElements.get('quick-view');
    var proto = QuickView && QuickView.prototype;
    if (!proto || proto.__cardSwatchPatched || typeof proto.quickview !== 'function') return;
    proto.__cardSwatchPatched = true;

    var original = proto.quickview;
    proto.quickview = function () {
      var variantId = this.getAttribute('data-variant-id');
      if (!variantId || !/^\d+$/.test(variantId)) return original.apply(this, arguments);

      // Same steps as theme.js QuickView#quickview, with the variant kept in the URL.
      var self = this;
      var drawerContent = this.querySelector(this.selector);
      var sectionUrl = this.getAttribute('data-product-url').split('?')[0] + '?view=modal&variant=' + variantId;
      if (this.quickviewAbortController) this.quickviewAbortController.abort();
      this.quickviewAbortController = new AbortController();
      var signal = this.quickviewAbortController.signal;

      fetch(sectionUrl, { signal: signal })
        .then(function (response) { return response.text(); })
        .then(function (responseText) {
          setTimeout(function () {
            if (signal.aborted) return;
            var parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            var productElement = (parsedHTML.getElementById('MainContent') || parsedHTML).querySelector(self.selector);
            if (!productElement) {
              console.error('[QuickView] Missing content in response:', sectionUrl);
              self.hide();
              return;
            }
            self.setInnerHTML(drawerContent, productElement.innerHTML);
            theme.a11y.trapFocus(self, self.focusElement);
            if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
            document.dispatchEvent(new CustomEvent('quickview:loaded', { detail: { productUrl: self.getAttribute('data-product-url') } }));
          }, 200);
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') console.error(error);
        });
    };
  }

  if (window.customElements) customElements.whenDefined('quick-view').then(patchQuickView);
})();
